import {Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";
import moment from "moment/moment";
import {update_apps, update_project_apps, delete_app} from "./graphql/alterApps";
import {Apollo} from "apollo-angular";
import {formatCurrency} from "@angular/common";
import {MatSnackBar, MatSnackBarConfig} from "@angular/material/snack-bar";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ConfirmationModalComponent} from "../../../../components/confirmation-modal/confirmation-modal.component";

const UPDATE_APPS = update_apps;
const UPDATE_PROJECT_APPS = update_project_apps;
const DELETE_APP = delete_app;

export interface TaxCreditAppsTable {
  id: string;
  projectID: string;
  fundingRoundID: string;
  orgID: string;
  credit: string;
  awarded: string;
  awardedOld: boolean | string;
  awardedNew: boolean | string;
  amount: string;
  amountOld: string;
  amountNew: string;
  requestDate: string;
  newDate: string;
  isEditMode: boolean;
}

@Component({
    selector: 'deal-tax-credit-applications-table',
    templateUrl: './tax-credit-applications-table.component.html',
    styleUrls: ['./tax-credit-applications-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaxCreditApplicationsTableComponent {
  @Input() apps: Array<TaxCreditAppsTable>;
  @Input() permissions: any;
  @Output() deleteFinancingTerm: EventEmitter<any> = new EventEmitter();
  @Output() projectStatusChange: EventEmitter<any> = new EventEmitter();
  @ViewChild('dataSort') public dataSort: MatSort;

  dataColumns: string[] = ['credit', 'awarded', 'amount', 'requestDate'];
  dataSource: MatTableDataSource<TaxCreditAppsTable>;

  constructor(private apollo: Apollo,
              private _snackBar: MatSnackBar,
              private dialog: MatDialog) {}

  ngOnInit() {
    this.dataSource = new MatTableDataSource(this.apps);
  }

  ngAfterViewInit(){
    this.dataSource.sort = this.dataSort;
  }

  toggleEditMode(row: any){
    if(row.isEditMode){
      let updated = [];
      let updatedOrg = [];
      let fundingRoundID: string | null = null;
      let projectID: string | null = null;
      let orgID: string | null = null;

      if(row.amountOld !== row.amountNew ||
      row.awardedOld !== row.awardedNew || row.requestDate !== row.newDate){
        fundingRoundID = row.fundingRoundID;
        orgID = row.orgID;
        projectID = row.projectID;

        let dataObj = {
          id: row.id,
          awarded: row.awardedNew === 'n/a' ? null : row.awardedNew,
          amount_requested: parseFloat(row.amountNew),
          request_date: row.newDate && moment(row.newDate).isValid() ? moment
            .utc(row.newDate)
            .format('YYYY-MM-DD') : null
        };

        if(fundingRoundID !== null){
          updated.push(dataObj);
        } else {
          updatedOrg.push(dataObj);
        }
      }

      if(updated.length > 0){
        this.apollo
          .mutate({
            mutation: UPDATE_APPS,
            variables: {
              input: {
                id: fundingRoundID,
                applications: {
                  update: updated
                }
              },
              projectid: projectID
            }
          })
          .subscribe(
            ({ data }) => {
              let newData: any = data;
              // @ts-ignore
              this.apps = this.formatApps(newData.updateApps.applications, fundingRoundID, orgID, projectID);

              if(newData.updateApps.applications.length > 0){
                this.projectStatusChange.emit({
                  statuses: newData.updateApps.applications[0].project.projectStatuses,
                  pipelines: newData.updateApps.applications[0].project.pipelineProjects,
                });
              }

              this.dataSource = new MatTableDataSource(this.apps);
              this.dataSource.sort = this.dataSort;
              this.openSnackBar('Saved Tax Credit Applications Successfully.');
            }
          );
      } else if (updatedOrg.length > 0){
        this.apollo
          .mutate({
            mutation: UPDATE_PROJECT_APPS,
            variables: {
              orgid: row.organizationID,
              input: {
                id: projectID,
                applications: {
                  update: updatedOrg
                }
              },
            }
          })
          .subscribe(
            ({ data }) => {
              let newData: any = data;

              // @ts-ignore
              this.apps = this.formatApps(newData.updateProjectApps.applications, fundingRoundID, orgID, projectID);

              this.projectStatusChange.emit({
                statuses: newData.updateProjectApps.projectStatuses,
                pipelines: newData.updateProjectApps.pipelineProjects
              });

              this.dataSource = new MatTableDataSource(this.apps);
              this.dataSource.sort = this.dataSort;
              this.openSnackBar('Saved Tax Credit Applications Successfully.');
            }
          );
      }
    }
    row.isEditMode = !row.isEditMode;
  }

  updateAmount(amount: any, i: number){
    this.apps[i].amountNew = amount.target.value;
  }

  changeDate(event: any, i: number){
    this.apps[i].newDate = new Date(moment.utc(event.value).format("MM/DD/YYYY")).toISOString();
  }

  formatApps(apps: any, fundingid: string, orgid: string, projectid: string): any{
    let appArray = [];
    for(let i = 0; i < apps.length; i++){
      let credit = apps[i]['taxCreditType']['level'] + " " + apps[i]['taxCreditType']['name'];
      if(apps[i]['taxCreditDesignation']){
        credit += " - " + apps[i]['taxCreditDesignation']['name'];
      }

      let awardedText = '';
      if(apps[i]['awarded'] !== null){
        awardedText = apps[i]['awarded'] ? "Awarded" : "Not Awarded";
      } else {
        awardedText = 'N/A'
      }

      appArray.push({
        id: apps[i]['id'],
        projectID: projectid,
        fundingRoundID: fundingid,
        orgID: orgid,
        credit: credit,
        awarded: awardedText,
        awardedOld: apps[i]['awarded'] !== null ? apps[i]['awarded'] : 'n/a',
        awardedNew: apps[i]['awarded'] !== null ? apps[i]['awarded'] : 'n/a',
        amount: formatCurrency(apps[i]['amount_requested'], 'EN', '$', 'USD', '1.0-0'),
        amountOld: apps[i]['amount_requested'] ? apps[i]['amount_requested'].toString() : null,
        amountNew: apps[i]['amount_requested'] ? apps[i]['amount_requested'].toString() : null,
        requestDate: apps[i]['request_date'] ? moment
          .utc(apps[i]['request_date'])
          .format('MM/DD/YYYY') : '-',
        newDate: apps[i]['request_date'] ? moment
          .utc(apps[i]['request_date'])
          .format('MM/DD/YYYY') : '-'
      });
    }
    return appArray;
  }

  deleteApp(row: any, i: number){
    const ref: MatDialogRef<ConfirmationModalComponent> = this.dialog.open(
      ConfirmationModalComponent,
      {
        width: '600px',
        height: '380px',
        data: {
          modalTitle: 'Delete Application',
          actionMessage: 'Are you sure you want to delete the Tax Credit Application?',
          modalInformation: [
            {
              title: 'Credit Type',
              info: row.credit
            },
            {
              title: 'Awarded',
              info: row.awarded
            },
            {
              title: 'Amount',
              info: row.amount
            },
            {
              title: 'Request Date',
              info: row.requestDate
            },
          ]
        },
        backdropClass: 'confirmDialogComponent',
        hasBackdrop: true
      });
    ref.afterClosed().subscribe((data) => {
      if(data.clicked === 'Ok'){
        this.apollo
          .mutate({
            mutation: DELETE_APP,
            variables: {
              id: row.id,
            }
          })
          .subscribe(
            ({ data }) => {
              // @ts-ignore
              this.deleteFinancingTerm.emit({fundingRoundID: row.fundingRoundID, appID: data['deleteApp'].id});
              this.openSnackBar('Removed Tax Credit Application Successfully.');
            }
          );
      }
    });
  }

  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = "center";
    config.verticalPosition = "top";
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }
}
